import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, RotateCcw, Car, Tag, Calendar, DollarSign, RefreshCcw, ArrowLeftRight, Video } from 'lucide-react';
import { usePromptMenu } from '@/hooks/usePromptMenu';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCertificationStyles } from '@/hooks/useCertificationStyles';

interface PromptMenuProps {
  onPromptSelect: (message: string) => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  onFormExpand?: (formType: string) => void;
}

const PromptMenu: React.FC<PromptMenuProps> = ({ onPromptSelect, inputValue, setInputValue, onFormExpand }) => {
  const {
    isEnabled,
    isLoading,
    currentLevelItems,
    currentLevel,
    handleButtonClick,
    buildMessageText,
    resetMenu,
    canGoBack,
    goBack,
    language
  } = usePromptMenu();
  
  const { t } = useLanguage();
  const { styles } = useCertificationStyles();
  
  const [justHandledAction, setJustHandledAction] = useState(false);
  const [userHasEdited, setUserHasEdited] = useState(false);

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'fa-car':
        return Car;
      case 'fa-tag':
        return Tag;
      case 'fa-calendar-days':
        return Calendar;
      case 'fa-dollar-sign':
        return DollarSign;
      case 'fa-rotate-reverse':
        return RefreshCcw;
      case 'fa-arrow-right-arrow-left':
      case 'fa-exchange':
      case 'fa-arrows-left-right':
        return ArrowLeftRight;
      case 'fa-video':
        return Video;
      default:
        return Car;
    }
  };

  useEffect(() => {
    resetMenu();
  }, []);

  useEffect(() => {
    if (inputValue !== buildMessageText() && inputValue !== '') {
      setUserHasEdited(true);
    }
  }, [inputValue, buildMessageText]);

  useEffect(() => {
    if (!justHandledAction && !userHasEdited) {
      const messageText = buildMessageText();
      if (messageText) {
        setInputValue(messageText);
      }
    }
  }, [buildMessageText, setInputValue, justHandledAction, userHasEdited]);

  if (!isEnabled || isLoading) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 d2cbot-rounded-xl d2cbot-p-3 d2cbot-bot_border d2cbot-bot_border-gray-200 d2cbot-mb-4 d2cbot-animate-fade-in">
      <div className="d2cbot-flex d2cbot-items-center d2cbot-justify-between d2cbot-mb-2 -d2cbot-mt-2">
        <div className="d2cbot-flex d2cbot-items-center d2cbot-space-x-2">
          {canGoBack && (
            <Button
              variant="ghost"
              size="sm"
              onClick={goBack}
              className="d2cbot-p-1 d2cbot-h-6 d2cbot-w-6 d2cbot-text-gray-600 d2cbot-hover-text-gray-800 d2cbot-hover-bg-gray-200"
            >
              <ArrowLeft className="h-3 w-3" />
            </Button>
          )}
          <span className="d2cbot-text-xs d2cbot-font-medium d2cbot-text-gray-600">
            {t('promptMenu.searching')}
          </span>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={resetMenu}
          className="d2cbot-p-1 d2cbot-h-6 d2cbot-w-6 d2cbot-text-gray-600 d2cbot-hover-text-gray-800 d2cbot-hover-bg-gray-200"
          title={t('promptMenu.reset')}
        >
          <RotateCcw className="h-3 w-3" />
        </Button>
      </div>

      {currentLevelItems.length > 0 ? (
        <div className="d2cbot-grid d2cbot-grid-cols-2 d2cbot-gap-2">
          {currentLevelItems.map((item) => {
            const IconComponent = item.icon ? getIconComponent(item.icon) : null;
            return (
              <Button
                  key={`${item.level}-${item.position}`}
                  variant="outline"
                  className="d2cbot-h-auto d2cbot-p-2 d2cbot-text-left d2cbot-flex d2cbot-flex-col d2cbot-items-start d2cbot-justify-start d2cbot-bg-white d2cbot-hover-bg-gray-50 d2cbot-bot_border-gray-300 hover:d2cbot-bot_border-gray-400 d2cbot-transition-all d2cbot-duration-200 justify-start"
                  style={{
                    height: 'auto',
                    minHeight: '40px',
                    maxHeight: '60px',
                    padding: '8px',
                    fontSize: '14px',
                    lineHeight: '1.25',
                    boxSizing: 'border-box'
                  }}
                  onClick={async () => {
                    window.dispatchEvent(new CustomEvent('stopSimulatedTyping'));
                    
                    const result = await handleButtonClick(item);
                    
                    if (result) {
                      if (result.startsWith('FORM_EXPAND:')) {
                        const formType = result.replace('FORM_EXPAND:', '');
                        setInputValue('');
                        if (onFormExpand) {
                          onFormExpand(formType);
                        }
                      } else if (result === 'CLEAR_MESSAGE_BOX') {
                        setInputValue('');
                      } else {
                        setInputValue(result);
                        setUserHasEdited(false);

                        // Final prompt-menu selections (not drill-down buttons) should execute immediately.
                        // Otherwise users see "Ready to send your message" with text in the composer and no results.
                        onPromptSelect(result);
                      }
                    } else {
                      const currentMessage = buildMessageText();
                      setInputValue(currentMessage || ' ');
                      setUserHasEdited(false);
                    }
                    setJustHandledAction(false);
                  }}
                >
                  <div className="d2cbot-flex d2cbot-items-center d2cbot-justify-between d2cbot-w-full d2cbot-gap-3" style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <div className="d2cbot-flex d2cbot-items-center d2cbot-gap-2 d2cbot-flex-shrink-0" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {IconComponent && currentLevel === 1 && (
                        <IconComponent className="d2cbot-h-4 d2cbot-w-4 d2cbot-text-gray-600 d2cbot-flex-shrink-0" style={{ width: '16px', height: '16px', flexShrink: 0 }} />
                      )}
                      <span className="d2cbot-font-bold d2cbot-text-sm d2cbot-text-gray-900 d2cbot-whitespace-nowrap" style={{ fontSize: '14px', fontWeight: 700, whiteSpace: 'nowrap' }}>{item.title}</span>
                    </div>
                    {item.count !== undefined && item.count > 0 && (
                      <span 
                        className="inline-flex items-center justify-center px-2 py-1 text-xs font-semibold rounded-full"
                        style={{
                          backgroundColor: styles.header_bg_col,
                          color: styles.header_text_col
                        }}
                      >
                        {item.count}
                      </span>
                    )}
                  </div>
                </Button>
            );
          })}
        </div>
      ) : (
        <div className="d2cbot-text-xs d2cbot-text-gray-600 d2cbot-text-center d2cbot-py-2">
          {t('promptMenu.readyToSend')}
        </div>
      )}
    </div>
  );
};

export default PromptMenu;
